/**
 * RADGRANSKARNA — klientens ENDA granskare för företagsdata.
 *
 * ============================================================================
 * DEN HÄR FILEN ERSÄTTER SJU KOPIOR AV SIG SJÄLV.
 * Fyra grupper byggde var sin skärm mot samma kontrakt, och var och en skrev
 * av hela granskarlagret: arbetsorder.ts, laget.ts, moment.ts, personal.ts,
 * priser.ts, synk.ts och tidrapport.ts bar alla en egen `granskaTidrad`,
 * `granskaKund`, `granskaSyncPull` och så vidare — 5 300 rader där cirka 700 var
 * innehållet. De hade redan börjat glida isär: samma värdemängd hette
 * PRISKALLOR, SATSKALLOR och PRISKALLORNA på olika ställen, och den fallna
 * operationens felfält hette 'invoice_run_id' i en fil och
 * 'time_entry.invoice_run_id' i en annan. Skillnaderna var kosmetiska DEN HÄR
 * gången. Nästa kontraktsändring hade blivit sju ändringar, och den som glömdes
 * hade blivit ett granskningsfel ute hos en kund.
 *
 * De sex övriga filerna är nu genomsläpp mot den här, och skärmarna importerar
 * som förut. laget.ts behåller sina EGNA svarsgranskare — TODAY och
 * INVOICE_BASIS är inte radgranskare — men läser raderna härifrån.
 * ============================================================================
 *
 * VARFÖR DEN ÄR KOMPLETT OCH INTE "DET STARTSIDAN BEHÖVER".
 * api/granska.ts: "RETURTYPEN ÄR BEVISET." En granskare som lämnar något annat
 * än kontraktets egen typ slutar falla när kontraktet ändras, och då är hela
 * poängen med att klienten importerar shared/ borta. SyncPullResponse är en
 * diskriminerad union över SEXTON tabeller i full vy och ELVA i montörs- och
 * gästvy, så granskaren blir lika stor. Den är skriven EN gång här och delas av
 * alla skärmar — startsidan, kalendern, ordrarna, tiden, milen och utläggen —
 * i stället för att fyra agenter skriver var sin halva.
 *
 * BESLUT 16 OCH 17 HÅLLS AV TYPERNA, INTE AV MIG.
 * `view` grenar ut i tre granskare med var sin returtyp. I montörs- och gästvy
 * finns rate_ore_per_hour, rate_role, rate_source, rate_price_list_id,
 * rot_hundredths och invoice_run_id INTE i TimeEntryWorkerRow — så en rad som
 * läst ut dem här hade varit ett kompileringsfel, inte ett läckage som upptäcks i
 * drift. Skulle servern ÄNDÅ skicka med prisfält i ett montörssvar plockas de
 * aldrig ur objektet: granskaren bygger en NY rad fält för fält och kopierar
 * aldrig `o` rakt av.
 *
 * TVÅ LUCKOR SOM ÄR RAPPORTERADE OCH INTE GLÖMDA — se överlämningen:
 *   1. api/granska.ts har id-granskare för sju av sjutton id-märken. De tio
 *      som fattas står nedan under `markOm`, som återvinner accountId:s
 *      formkontroll (samma regel: 20-40 tecken ur [A-Za-z0-9_-]) och sätter om
 *      märket. De hör hemma i api/granska.ts bredvid de andra.
 *   2. shared/types.ts ger literalunionerna WeekState, CustomerKind,
 *      WorkOrderStatus, EntrySource, RateSource, Vehicle, AbsenceKind och
 *      MaterialUnit som TYPER men inte som DATA. Listorna står därför här, var
 *      och en med en `Uttommande`-rad som gör ett tillagt värde i kontraktet
 *      till ett kompileringsfel i den här filen — samma grepp som
 *      PRENUMERATIONSLAGEN i granskare/konto.ts.
 */

import { ALL_ROLES, REPORTING_ROLES } from '@delat/routes';
import { SYSTEM_ACTOR } from '@delat/types';
import type {
  AbsenceId,
  AbsenceKind,
  AbsenceRow,
  Actor,
  ClientView,
  CompanySettingsRow,
  CustomerKind,
  CustomerRow,
  CustomerWorkerRow,
  DeviceId,
  EntrySource,
  ExpenseId,
  ExpenseRow,
  ExpenseWorkerRow,
  FullChangeSet,
  GuestChangeSet,
  InvoiceRunRow,
  MaterialLineId,
  MaterialLineRow,
  MaterialLineWorkerRow,
  MaterialUnit,
  MemberRow,
  MemberWorkerRow,
  MileageId,
  MileageRow,
  MileageWorkerRow,
  PriceListId,
  PriceListRow,
  PriceRateId,
  PriceRateRow,
  RateSource,
  SyncColumns,
  SyncOpResult,
  SyncPullResponse,
  SyncPushResponse,
  TaskRow,
  TimeEntryId,
  TimeEntryRow,
  TimeEntryWorkerRow,
  Vehicle,
  WeekState,
  WeekStatusRow,
  WorkerChangeSet,
  WorkOrderGuestId,
  WorkOrderGuestRow,
  WorkOrderPhotoId,
  WorkOrderPhotoRow,
  WorkOrderRow,
  WorkOrderStatus,
  WorkOrderWorkerRow,
} from '@delat/types';
import {
  Granskningsfel,
  accountId,
  arbetsdag,
  customerId,
  enAv,
  flagga,
  heltal,
  hundradelar,
  invoiceRunId,
  isovecka,
  lista,
  meter,
  millisekunder,
  minuter,
  nullbar,
  objectKey,
  objekt,
  opId,
  oren,
  orePerEnhet,
  orePerMil,
  orePerTimme,
  orgnummer,
  seq,
  taskId,
  text,
  tusendel,
  version,
  workOrderId,
} from '../granska';
import type { Granskare } from '../granska';
import type { ApiErrorBody, ErrorCode, ErrorDetail, ErrorDetailValue } from '@delat/errors';
import { apifel, arKandFelkod } from '../fel';
import type { Apifel } from '../fel';
import { granskaLicensbevis } from './konto';

/* == De tio id-märken api/granska.ts inte har ============================= */

/**
 * Formen prövas med granska.accountId — id-regeln är EN och densamma för varje
 * märke (worker/src/util/ids.ts requireIdText) — och märket sätts sedan om.
 * Det är samma enda `as <id-marke>` som api/granska.ts redan gör, och det
 * ligger på ETT ställe här precis som där.
 */
const markOm = <TMarke extends string>(varde: unknown, falt: string): TMarke =>
  accountId(varde, falt) as string as TMarke;

/**
 * DE ÄR EXPORTERADE, för skärmarnas skrivsida behöver dem också: en ny tidrad
 * ska ha ett id som klarar SAMMA formkontroll som en läst rad. Tidigare fanns de
 * i tre filer under tre namn (tidradId, timeEntryId, idMarke<TimeEntryId>).
 */
export const deviceId = (v: unknown, f: string): DeviceId => markOm(v, f);
export const timeEntryId = (v: unknown, f: string): TimeEntryId => markOm(v, f);
export const materialLineId = (v: unknown, f: string): MaterialLineId => markOm(v, f);
export const mileageId = (v: unknown, f: string): MileageId => markOm(v, f);
export const expenseId = (v: unknown, f: string): ExpenseId => markOm(v, f);
export const absenceId = (v: unknown, f: string): AbsenceId => markOm(v, f);
export const priceListId = (v: unknown, f: string): PriceListId => markOm(v, f);
export const priceRateId = (v: unknown, f: string): PriceRateId => markOm(v, f);
export const workOrderGuestId = (v: unknown, f: string): WorkOrderGuestId => markOm(v, f);
export const workOrderPhotoId = (v: unknown, f: string): WorkOrderPhotoId => markOm(v, f);

/* == Literalunionerna som data =========================================== */

export const KLIENTVYER = ['full', 'worker', 'guest'] as const satisfies readonly ClientView[];
export const VECKOLAGEN = [
  'open',
  'submitted',
  'approved',
  'locked',
  'reopened',
] as const satisfies readonly WeekState[];
export const KUNDSLAGEN = ['private', 'company'] as const satisfies readonly CustomerKind[];
export const ORDERLAGEN = ['open', 'done'] as const satisfies readonly WorkOrderStatus[];
export const KALLORNA = ['manual', 'voice'] as const satisfies readonly EntrySource[];
export const PRISKALLORNA = [
  'work_order',
  'customer',
  'company',
] as const satisfies readonly RateSource[];
export const FORDONEN = ['own_car', 'company_car'] as const satisfies readonly Vehicle[];
export const FRANVAROSLAGEN = [
  'vacation',
  'leave',
  'other',
] as const satisfies readonly AbsenceKind[];
export const MATERIALENHETERNA = [
  'pcs',
  'm',
  'm2',
  'm3',
  'kg',
  'l',
  'pkg',
] as const satisfies readonly MaterialUnit[];

/**
 * BEVISET ATT LISTORNA OVAN ÄR UTTÖMMANDE. Läggs ett värde till i kontraktet
 * blir motsvarande typ `never` och tsc faller HÄR — i stället för att
 * granskaren tyst börjar avvisa det nya värdet ute hos en kund.
 */
type Tacker<TUnion extends string, TLista extends readonly string[]> = [TUnion] extends [
  TLista[number],
]
  ? true
  : never;

export type UttommandeVyer = Tacker<ClientView, typeof KLIENTVYER>;
export type UttommandeVeckolagen = Tacker<WeekState, typeof VECKOLAGEN>;
export type UttommandeKundslagen = Tacker<CustomerKind, typeof KUNDSLAGEN>;
export type UttommandeOrderlagen = Tacker<WorkOrderStatus, typeof ORDERLAGEN>;
export type UttommandeKallorna = Tacker<EntrySource, typeof KALLORNA>;
export type UttommandePriskallorna = Tacker<RateSource, typeof PRISKALLORNA>;
export type UttommandeFordonen = Tacker<Vehicle, typeof FORDONEN>;
export type UttommandeFranvaroslagen = Tacker<AbsenceKind, typeof FRANVAROSLAGEN>;
export type UttommandeMaterialenheterna = Tacker<MaterialUnit, typeof MATERIALENHETERNA>;

/* == Små former =========================================================== */

/** 0 eller 1. schema/company.sql lagrar flaggor så, och typen säger `0 | 1`. */
const flaggtal = (varde: unknown, falt: string): 0 | 1 => {
  const n = heltal(varde, falt);
  if (n === 0) return 0;
  if (n === 1) return 1;
  throw new Granskningsfel(falt, 'förväntade 0 eller 1');
};

/** Actor = AccountId | 'system'. Cron och Stripe skriver som 'system'. */
const aktor = (varde: unknown, falt: string): Actor =>
  varde === SYSTEM_ACTOR ? SYSTEM_ACTOR : accountId(varde, falt);

const nullbarText = (varde: unknown, falt: string): string | null =>
  nullbar(varde, falt, (x) => text(x, falt));

/**
 * De sju kolumner varje synkad rad bär. Skrivs en gång och sprids in i varje
 * rad — tjugo handskrivna kopior hade blivit nitton likadana och en med ett
 * fält som glömts bort.
 */
const synkkolumner = (o: Record<string, unknown>, p: string): SyncColumns => ({
  version: version(o['version'], `${p}.version`),
  deleted_at: nullbar(o['deleted_at'], `${p}.deleted_at`, (x) =>
    millisekunder(x, `${p}.deleted_at`),
  ),
  created_by: aktor(o['created_by'], `${p}.created_by`),
  updated_by: aktor(o['updated_by'], `${p}.updated_by`),
  device_id: nullbar(o['device_id'], `${p}.device_id`, (x) => deviceId(x, `${p}.device_id`)),
  server_received_at: millisekunder(o['server_received_at'], `${p}.server_received_at`),
  seq: seq(o['seq'], `${p}.seq`),
});

/* == Raderna ============================================================== */

export const granskaFirmainstallningar: Granskare<CompanySettingsRow> = (v) => {
  const o = objekt(v, 'company_settings');
  const id = heltal(o['id'], 'company_settings.id');
  if (id !== 1) throw new Granskningsfel('company_settings.id', 'förväntade 1');
  return {
    ...synkkolumner(o, 'company_settings'),
    id: 1,
    mileage_ore_per_10km: orePerMil(
      o['mileage_ore_per_10km'],
      'company_settings.mileage_ore_per_10km',
    ),
    vat_rate_hundredths: hundradelar(
      o['vat_rate_hundredths'],
      'company_settings.vat_rate_hundredths',
    ),
  };
};

export const granskaMedlem: Granskare<MemberRow> = (v) => {
  const o = objekt(v, 'member');
  return {
    ...synkkolumner(o, 'member'),
    account_id: accountId(o['account_id'], 'member.account_id'),
    email: text(o['email'], 'member.email'),
    name: text(o['name'], 'member.name'),
    role: enAv(o['role'], 'member.role', ALL_ROLES),
    guest_org_number: nullbar(o['guest_org_number'], 'member.guest_org_number', (x) =>
      orgnummer(x, 'member.guest_org_number'),
    ),
    is_owner: flaggtal(o['is_owner'], 'member.is_owner'),
  };
};

/** Beslut 16/17: e-post, gäst-org.nr och ägarflagga går inte ut till en telefon. */
export const granskaMedlemMontor: Granskare<MemberWorkerRow> = (v) => {
  const o = objekt(v, 'member');
  return {
    ...synkkolumner(o, 'member'),
    account_id: accountId(o['account_id'], 'member.account_id'),
    name: text(o['name'], 'member.name'),
    role: enAv(o['role'], 'member.role', ALL_ROLES),
  };
};

export const granskaPrislista: Granskare<PriceListRow> = (v) => {
  const o = objekt(v, 'price_list');
  return {
    ...synkkolumner(o, 'price_list'),
    id: priceListId(o['id'], 'price_list.id'),
    name: text(o['name'], 'price_list.name'),
    is_default: flaggtal(o['is_default'], 'price_list.is_default'),
  };
};

export const granskaPrisrad: Granskare<PriceRateRow> = (v) => {
  const o = objekt(v, 'price_rate');
  return {
    ...synkkolumner(o, 'price_rate'),
    id: priceRateId(o['id'], 'price_rate.id'),
    price_list_id: priceListId(o['price_list_id'], 'price_rate.price_list_id'),
    // REPORTING_ROLES är kontraktets egen PriceRole-mängd. Bokföraren har
    // aldrig ett timpris (beslut 07 och 15).
    role: enAv(o['role'], 'price_rate.role', REPORTING_ROLES),
    ore_per_hour: orePerTimme(o['ore_per_hour'], 'price_rate.ore_per_hour'),
  };
};

export const granskaKund: Granskare<CustomerRow> = (v) => {
  const o = objekt(v, 'customer');
  return {
    ...synkkolumner(o, 'customer'),
    id: customerId(o['id'], 'customer.id'),
    name: text(o['name'], 'customer.name'),
    kind: enAv(o['kind'], 'customer.kind', KUNDSLAGEN),
    org_number: nullbar(o['org_number'], 'customer.org_number', (x) =>
      orgnummer(x, 'customer.org_number'),
    ),
    address: nullbarText(o['address'], 'customer.address'),
    price_list_id: nullbar(o['price_list_id'], 'customer.price_list_id', (x) =>
      priceListId(x, 'customer.price_list_id'),
    ),
    rot_hundredths: nullbar(o['rot_hundredths'], 'customer.rot_hundredths', (x) =>
      hundradelar(x, 'customer.rot_hundredths'),
    ),
  };
};

/** Beslut 16: kunden utan sina prispekare. */
export const granskaKundMontor: Granskare<CustomerWorkerRow> = (v) => {
  const o = objekt(v, 'customer');
  return {
    ...synkkolumner(o, 'customer'),
    id: customerId(o['id'], 'customer.id'),
    name: text(o['name'], 'customer.name'),
    kind: enAv(o['kind'], 'customer.kind', KUNDSLAGEN),
    org_number: nullbar(o['org_number'], 'customer.org_number', (x) =>
      orgnummer(x, 'customer.org_number'),
    ),
    address: nullbarText(o['address'], 'customer.address'),
  };
};

export const granskaArbetsorder: Granskare<WorkOrderRow> = (v) => {
  const o = objekt(v, 'work_order');
  return {
    ...synkkolumner(o, 'work_order'),
    id: workOrderId(o['id'], 'work_order.id'),
    customer_id: customerId(o['customer_id'], 'work_order.customer_id'),
    address: nullbarText(o['address'], 'work_order.address'),
    description: text(o['description'], 'work_order.description'),
    status: enAv(o['status'], 'work_order.status', ORDERLAGEN),
    responsible_account_id: nullbar(
      o['responsible_account_id'],
      'work_order.responsible_account_id',
      (x) => accountId(x, 'work_order.responsible_account_id'),
    ),
    price_list_id: nullbar(o['price_list_id'], 'work_order.price_list_id', (x) =>
      priceListId(x, 'work_order.price_list_id'),
    ),
  };
};

export const granskaArbetsorderMontor: Granskare<WorkOrderWorkerRow> = (v) => {
  const o = objekt(v, 'work_order');
  return {
    ...synkkolumner(o, 'work_order'),
    id: workOrderId(o['id'], 'work_order.id'),
    customer_id: customerId(o['customer_id'], 'work_order.customer_id'),
    address: nullbarText(o['address'], 'work_order.address'),
    description: text(o['description'], 'work_order.description'),
    status: enAv(o['status'], 'work_order.status', ORDERLAGEN),
    responsible_account_id: nullbar(
      o['responsible_account_id'],
      'work_order.responsible_account_id',
      (x) => accountId(x, 'work_order.responsible_account_id'),
    ),
  };
};

export const granskaOrdergast: Granskare<WorkOrderGuestRow> = (v) => {
  const o = objekt(v, 'work_order_guest');
  return {
    ...synkkolumner(o, 'work_order_guest'),
    id: workOrderGuestId(o['id'], 'work_order_guest.id'),
    work_order_id: workOrderId(o['work_order_id'], 'work_order_guest.work_order_id'),
    account_id: accountId(o['account_id'], 'work_order_guest.account_id'),
  };
};

export const granskaOrderfoto: Granskare<WorkOrderPhotoRow> = (v) => {
  const o = objekt(v, 'work_order_photo');
  return {
    ...synkkolumner(o, 'work_order_photo'),
    id: workOrderPhotoId(o['id'], 'work_order_photo.id'),
    work_order_id: workOrderId(o['work_order_id'], 'work_order_photo.work_order_id'),
    object_key: objectKey(o['object_key'], 'work_order_photo.object_key'),
  };
};

export const granskaMoment: Granskare<TaskRow> = (v) => {
  const o = objekt(v, 'task');
  return {
    ...synkkolumner(o, 'task'),
    id: taskId(o['id'], 'task.id'),
    name: text(o['name'], 'task.name'),
    rot_eligible: flaggtal(o['rot_eligible'], 'task.rot_eligible'),
  };
};

export const granskaFakturakorning: Granskare<InvoiceRunRow> = (v) => {
  const o = objekt(v, 'invoice_run');
  return {
    ...synkkolumner(o, 'invoice_run'),
    id: invoiceRunId(o['id'], 'invoice_run.id'),
    from_date: arbetsdag(o['from_date'], 'invoice_run.from_date'),
    to_date: arbetsdag(o['to_date'], 'invoice_run.to_date'),
    customer_id: nullbar(o['customer_id'], 'invoice_run.customer_id', (x) =>
      customerId(x, 'invoice_run.customer_id'),
    ),
    work_order_id: nullbar(o['work_order_id'], 'invoice_run.work_order_id', (x) =>
      workOrderId(x, 'invoice_run.work_order_id'),
    ),
    note: nullbarText(o['note'], 'invoice_run.note'),
  };
};

export const granskaTidrad: Granskare<TimeEntryRow> = (v) => {
  const o = objekt(v, 'time_entry');
  return {
    ...synkkolumner(o, 'time_entry'),
    id: timeEntryId(o['id'], 'time_entry.id'),
    account_id: accountId(o['account_id'], 'time_entry.account_id'),
    work_date: arbetsdag(o['work_date'], 'time_entry.work_date'),
    minutes: minuter(o['minutes'], 'time_entry.minutes'),
    task_id: taskId(o['task_id'], 'time_entry.task_id'),
    work_order_id: workOrderId(o['work_order_id'], 'time_entry.work_order_id'),
    note: nullbarText(o['note'], 'time_entry.note'),
    // Beslut 15: satserna är FRYSTA av servern. Klienten läser dem, skickar
    // dem aldrig — TimeEntryWrite kan inte ens uttrycka dem.
    rate_ore_per_hour: orePerTimme(o['rate_ore_per_hour'], 'time_entry.rate_ore_per_hour'),
    rate_role: enAv(o['rate_role'], 'time_entry.rate_role', REPORTING_ROLES),
    rate_source: enAv(o['rate_source'], 'time_entry.rate_source', PRISKALLORNA),
    rate_price_list_id: priceListId(
      o['rate_price_list_id'],
      'time_entry.rate_price_list_id',
    ),
    rot_hundredths: hundradelar(o['rot_hundredths'], 'time_entry.rot_hundredths'),
    source: enAv(o['source'], 'time_entry.source', KALLORNA),
    invoice_run_id: nullbar(o['invoice_run_id'], 'time_entry.invoice_run_id', (x) =>
      invoiceRunId(x, 'time_entry.invoice_run_id'),
    ),
  };
};

/**
 * BESLUT 16 I KLARTEXT: här finns inget rate_-fält att läsa ut, ingen
 * rot_hundredths och ingen invoice_run_id. Lade någon till en rad för dem
 * skulle tsc falla på returtypen.
 */
export const granskaTidradMontor: Granskare<TimeEntryWorkerRow> = (v) => {
  const o = objekt(v, 'time_entry');
  return {
    ...synkkolumner(o, 'time_entry'),
    id: timeEntryId(o['id'], 'time_entry.id'),
    account_id: accountId(o['account_id'], 'time_entry.account_id'),
    work_date: arbetsdag(o['work_date'], 'time_entry.work_date'),
    minutes: minuter(o['minutes'], 'time_entry.minutes'),
    task_id: taskId(o['task_id'], 'time_entry.task_id'),
    work_order_id: workOrderId(o['work_order_id'], 'time_entry.work_order_id'),
    note: nullbarText(o['note'], 'time_entry.note'),
    source: enAv(o['source'], 'time_entry.source', KALLORNA),
  };
};

export const granskaMaterialrad: Granskare<MaterialLineRow> = (v) => {
  const o = objekt(v, 'material_line');
  return {
    ...granskaMaterialradMontor(v),
    invoice_run_id: nullbar(o['invoice_run_id'], 'material_line.invoice_run_id', (x) =>
      invoiceRunId(x, 'material_line.invoice_run_id'),
    ),
  };
};

/** unit_price_ore är ett MATERIALpris per enhet — enheten 'h' finns inte. */
export const granskaMaterialradMontor: Granskare<MaterialLineWorkerRow> = (v) => {
  const o = objekt(v, 'material_line');
  return {
    ...synkkolumner(o, 'material_line'),
    id: materialLineId(o['id'], 'material_line.id'),
    work_order_id: workOrderId(o['work_order_id'], 'material_line.work_order_id'),
    account_id: accountId(o['account_id'], 'material_line.account_id'),
    work_date: arbetsdag(o['work_date'], 'material_line.work_date'),
    description: text(o['description'], 'material_line.description'),
    quantity_milli: tusendel(o['quantity_milli'], 'material_line.quantity_milli'),
    unit: enAv(o['unit'], 'material_line.unit', MATERIALENHETERNA),
    unit_price_ore: orePerEnhet(o['unit_price_ore'], 'material_line.unit_price_ore'),
  };
};

export const granskaMilrad: Granskare<MileageRow> = (v) => {
  const o = objekt(v, 'mileage');
  return {
    ...granskaMilradMontor(v),
    rate_ore_per_10km: orePerMil(o['rate_ore_per_10km'], 'mileage.rate_ore_per_10km'),
    invoice_run_id: nullbar(o['invoice_run_id'], 'mileage.invoice_run_id', (x) =>
      invoiceRunId(x, 'mileage.invoice_run_id'),
    ),
  };
};

export const granskaMilradMontor: Granskare<MileageWorkerRow> = (v) => {
  const o = objekt(v, 'mileage');
  return {
    ...synkkolumner(o, 'mileage'),
    id: mileageId(o['id'], 'mileage.id'),
    account_id: accountId(o['account_id'], 'mileage.account_id'),
    work_date: arbetsdag(o['work_date'], 'mileage.work_date'),
    meters: meter(o['meters'], 'mileage.meters'),
    work_order_id: workOrderId(o['work_order_id'], 'mileage.work_order_id'),
    vehicle: enAv(o['vehicle'], 'mileage.vehicle', FORDONEN),
  };
};

export const granskaUtlagg: Granskare<ExpenseRow> = (v) => {
  const o = objekt(v, 'expense');
  return {
    ...granskaUtlaggMontor(v),
    invoice_run_id: nullbar(o['invoice_run_id'], 'expense.invoice_run_id', (x) =>
      invoiceRunId(x, 'expense.invoice_run_id'),
    ),
  };
};

/** amount_ore är EX MOMS — kvittots totalsumma får aldrig hamna där. */
export const granskaUtlaggMontor: Granskare<ExpenseWorkerRow> = (v) => {
  const o = objekt(v, 'expense');
  return {
    ...synkkolumner(o, 'expense'),
    id: expenseId(o['id'], 'expense.id'),
    account_id: accountId(o['account_id'], 'expense.account_id'),
    work_date: arbetsdag(o['work_date'], 'expense.work_date'),
    amount_ore: oren(o['amount_ore'], 'expense.amount_ore'),
    description: text(o['description'], 'expense.description'),
    receipt_object_key: nullbar(
      o['receipt_object_key'],
      'expense.receipt_object_key',
      (x) => objectKey(x, 'expense.receipt_object_key'),
    ),
    billable: flaggtal(o['billable'], 'expense.billable'),
    work_order_id: nullbar(o['work_order_id'], 'expense.work_order_id', (x) =>
      workOrderId(x, 'expense.work_order_id'),
    ),
  };
};

export const granskaFranvaro: Granskare<AbsenceRow> = (v) => {
  const o = objekt(v, 'absence');
  return {
    ...synkkolumner(o, 'absence'),
    id: absenceId(o['id'], 'absence.id'),
    account_id: accountId(o['account_id'], 'absence.account_id'),
    work_date: arbetsdag(o['work_date'], 'absence.work_date'),
    kind: enAv(o['kind'], 'absence.kind', FRANVAROSLAGEN),
  };
};

/** Beslut 04: veckan, aldrig månaden. Nyckeln är (account_id, iso_week). */
export const granskaVeckolage: Granskare<WeekStatusRow> = (v) => {
  const o = objekt(v, 'week_status');
  return {
    ...synkkolumner(o, 'week_status'),
    account_id: accountId(o['account_id'], 'week_status.account_id'),
    iso_week: isovecka(o['iso_week'], 'week_status.iso_week'),
    state: enAv(o['state'], 'week_status.state', VECKOLAGEN),
  };
};

/* == Ändringsmängderna =================================================== */

export const granskaFullaAndringar: Granskare<FullChangeSet> = (v) => {
  const o = objekt(v, 'changes');
  return {
    company_settings: lista(o['company_settings'], 'company_settings', granskaFirmainstallningar),
    member: lista(o['member'], 'member', granskaMedlem),
    price_list: lista(o['price_list'], 'price_list', granskaPrislista),
    price_rate: lista(o['price_rate'], 'price_rate', granskaPrisrad),
    customer: lista(o['customer'], 'customer', granskaKund),
    work_order: lista(o['work_order'], 'work_order', granskaArbetsorder),
    work_order_guest: lista(o['work_order_guest'], 'work_order_guest', granskaOrdergast),
    work_order_photo: lista(o['work_order_photo'], 'work_order_photo', granskaOrderfoto),
    task: lista(o['task'], 'task', granskaMoment),
    invoice_run: lista(o['invoice_run'], 'invoice_run', granskaFakturakorning),
    time_entry: lista(o['time_entry'], 'time_entry', granskaTidrad),
    material_line: lista(o['material_line'], 'material_line', granskaMaterialrad),
    mileage: lista(o['mileage'], 'mileage', granskaMilrad),
    expense: lista(o['expense'], 'expense', granskaUtlagg),
    absence: lista(o['absence'], 'absence', granskaFranvaro),
    week_status: lista(o['week_status'], 'week_status', granskaVeckolage),
  };
};

/**
 * BESLUT 16. Fem tabeller finns inte alls här: company_settings, price_list,
 * price_rate, work_order_guest och invoice_run. Skulle servern skicka dem läses
 * de aldrig — objektet byggs fält för fält ur listan nedan.
 */
export const granskaMontorsandringar: Granskare<WorkerChangeSet> = (v) => {
  const o = objekt(v, 'changes');
  return {
    member: lista(o['member'], 'member', granskaMedlemMontor),
    customer: lista(o['customer'], 'customer', granskaKundMontor),
    work_order: lista(o['work_order'], 'work_order', granskaArbetsorderMontor),
    work_order_photo: lista(o['work_order_photo'], 'work_order_photo', granskaOrderfoto),
    task: lista(o['task'], 'task', granskaMoment),
    time_entry: lista(o['time_entry'], 'time_entry', granskaTidradMontor),
    material_line: lista(o['material_line'], 'material_line', granskaMaterialradMontor),
    mileage: lista(o['mileage'], 'mileage', granskaMilradMontor),
    expense: lista(o['expense'], 'expense', granskaUtlaggMontor),
    absence: lista(o['absence'], 'absence', granskaFranvaro),
    week_status: lista(o['week_status'], 'week_status', granskaVeckolage),
  };
};

/**
 * BESLUT 17. Gästens mängd har i dag SAMMA form som montörens — urvalet
 * skiljer sig (bara tilldelade ordrar, bara egna materialrader), och det är
 * SERVERNS jobb. Raden nedan är ändå inte en genväg: skulle GuestChangeSet få
 * ett eget fält faller tsc här och tvingar fram en egen granskare.
 */
export const granskaGastandringar: Granskare<GuestChangeSet> = (v) => granskaMontorsandringar(v);

/* == Svaret =============================================================== */

/**
 * SYNC_PULL. Markören: `next_since` är högsta seq servern LÄSTE — även för
 * rader som vyn filtrerade bort — och `has_more: true` med tom `changes` är
 * NORMALT i montörs- och gästläge. Den som anropar måste följa markören och
 * inte antalet rader; se SyncPullResponse i shared/types.ts.
 */
export const granskaSyncPull: Granskare<SyncPullResponse> = (v) => {
  const o = objekt(v, 'sync_pull');
  const gemensamt = {
    since: seq(o['since'], 'since'),
    next_since: seq(o['next_since'], 'next_since'),
    has_more: flagga(o['has_more'], 'has_more'),
    license: granskaLicensbevis(o['license']),
    server_time: millisekunder(o['server_time'], 'server_time'),
  };
  const vy = enAv(o['view'], 'view', KLIENTVYER);
  switch (vy) {
    case 'full':
      return { ...gemensamt, view: 'full', changes: granskaFullaAndringar(o['changes']) };
    case 'worker':
      return { ...gemensamt, view: 'worker', changes: granskaMontorsandringar(o['changes']) };
    case 'guest':
      return { ...gemensamt, view: 'guest', changes: granskaGastandringar(o['changes']) };
  }
};

/* == SYNC_PUSH ============================================================
   Kuvertets svar, och den fallna operationens felkropp. Ett giltigt kuvert
   svarar ALLTID 200 även om varje operation föll — utfallet står i SyncOpResult
   och aldrig i HTTP-statusen.
   ======================================================================== */

const OPUTFALLEN = [
  'applied',
  'duplicate',
  'failed',
] as const satisfies readonly SyncOpResult['status'][];

export type UttommandeOputfallen = Tacker<SyncOpResult['status'], typeof OPUTFALLEN>;

/**
 * En felkropp inuti ett SyncOpResult. Bara code, message_sv och detail bärs där
 * — http_status gäller bara när HELA anropet faller (shared/errors.ts).
 *
 * DEN ENDA CASTEN I AVSNITTET, och shared/errors.ts har redan skrivit ut varför
 * den behövs: "en union går inte att sätta ihop ur en GENERISK code: ErrorCode".
 * Koden är först känd vid körning, så kroppen byggs och märks HÄR, på ett
 * ställe, precis som apifel() gör i api/fel.ts.
 */
const byggFelkropp = (
  code: ErrorCode,
  message_sv: string,
  detail: ErrorDetail | undefined,
): ApiErrorBody =>
  ({ code, message_sv, ...(detail === undefined ? {} : { detail }) }) as ApiErrorBody;

const granskaFeldetalj = (varde: unknown, falt: string): ErrorDetail => {
  const o = objekt(varde, falt);
  const ut: Record<string, ErrorDetailValue> = {};
  for (const [namn, x] of Object.entries(o)) {
    if (x === null || typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean') {
      ut[namn] = x;
    } else {
      throw new Granskningsfel(`${falt}.${namn}`, 'förväntade sträng, tal, boolean eller null');
    }
  }
  return ut;
};

export const granskaFelkropp: Granskare<ApiErrorBody> = (v) => {
  const o = objekt(v, 'error');
  const kod = o['code'];
  // arKandFelkod är api/fel.ts egen: den slår upp i ERRORS och inte i en
  // avskriven lista. En andra kopia av de tjugofem koderna hade varit exakt den
  // sortens divergens kontraktet finns för att göra omöjlig.
  if (!arKandFelkod(kod)) {
    throw new Granskningsfel('error.code', `okänd felkod ${String(kod)}`);
  }
  const detalj = o['detail'];
  return byggFelkropp(
    kod,
    text(o['message_sv'], 'error.message_sv'),
    detalj === undefined || detalj === null ? undefined : granskaFeldetalj(detalj, 'error.detail'),
  );
};

export const granskaOpresultat: Granskare<SyncOpResult> = (v) => {
  const o = objekt(v, 'result');
  const op = opId(o['op_id'], 'result.op_id');
  const utfall = enAv(o['status'], 'result.status', OPUTFALLEN);
  if (utfall === 'failed') {
    return { op_id: op, status: 'failed', error: granskaFelkropp(o['error']) };
  }
  return {
    op_id: op,
    status: utfall,
    version: version(o['version'], 'result.version'),
    seq: seq(o['seq'], 'result.seq'),
  };
};

export const granskaSyncPush: Granskare<SyncPushResponse> = (v) => {
  const o = objekt(v, 'sync_push');
  return {
    results: lista(o['results'], 'results', granskaOpresultat),
    license: granskaLicensbevis(o['license']),
    server_time: millisekunder(o['server_time'], 'server_time'),
  };
};

/**
 * En fallen operation som något gränssnittet kan VISA. Texten hämtas ur
 * klientens egen ERRORS-kopia (api/fel.ts), aldrig ur serverns sträng — se
 * filhuvudet där.
 */
export const somKlartidfel = (kropp: ApiErrorBody): Apifel =>
  apifel(kropp.code, kropp.detail, kropp.message_sv);
